import {
  Typography,
  TypographyColors,
  TypographyVariants
} from "@certa/catalyst/components/Typography";
import { Stack } from "@certa/catalyst/layouts/Stack";
import { useIntl } from "react-intl";
import { useSearchStore } from "../../store/useSearchStore";
import { advancedFilterStyles as styles } from "./AdvancedFilter.styles";
import { AdvancedFilters } from "./AdvancedFilters";
import { useEffect, useMemo, useState } from "react";
import type { ReportCustomFilter } from "@certa/types/src/filters";
import { CompareType } from "@certa/types/src/filters";
import { classNames } from "@certa/catalyst/utils/common";
import { analyticsService, AnalyticsEvents } from "@certa/analytics";
import { usePrevious } from "../../../../hooks";
import { Cascader } from "@certa/catalyst/components/Cascader";
import { getLayoutContainer } from "@certa/blocks/thanos";
import { useKinds } from "@certa/queries/hooks/kinds.hooks";
import { useGetObjectTypeTranslations } from "@certa/queries/hooks/dataObjects.hooks";
import { useObjectTypeTranslationsManager } from "@certa/common/store/dataObjects";
import { useGetDataObjectOptions } from "../../hooks/useGetDataObjectOptions";
import { useGetKindOptions } from "../../hooks/useGetKindOptions";
import { ObjectsAdvancedFilter } from "../../../ObjectsAdvancedFilter";
import { getOS3SearchFieldsFromObjectTypeData } from "../../../ObjectsAdvancedFilter/utils";
import { OS3_SEARCH_OPERATORS_BY_FIELD_TYPE } from "../../../ObjectsAdvancedFilter/constants";
import { SEARCH_RESULT_TYPES } from "../../GlobalSearchWithSDO.constants";

export const AdvancedFilterWrapper = () => {
  const intl = useIntl();

  const {
    areAdvancedFiltersVisible,
    selectedKindIdForAdvancedFilters,
    setSelectedKindIdForAdvancedFilters,
    setSelectedDataObjectTagForAdvancedFilters,
    areAdvancedFiltersApplied,
    advancedFilterConditions,
    selectedSearchTypeForAdvancedFilters,
    setDataObjectAdvancedFilters,
    selectedDataObjectTagForAdvancedFilters,
    clearAdvancedFilters,
    dataObjectAdvancedFilters,
    setSelectedResultType
  } = useSearchStore();

  const [advancedFilterCustomFilter, setAdvancedFilterCustomFilter] =
    useState<ReportCustomFilter>({
      groups: [],
      conditions: advancedFilterConditions,
      compareType: CompareType.AND
    });

  const { data: kindsList } = useKinds();
  const kindOptions = useGetKindOptions(kindsList);

  const { data: dataObjects } = useGetObjectTypeTranslations({
    config: {
      enabled: true
    }
  });
  const dataObjectOptions = useGetDataObjectOptions(dataObjects);

  const { getObjectTypeByTag } = useObjectTypeTranslationsManager();

  const fieldOptions = useMemo(() => {
    if (!selectedDataObjectTagForAdvancedFilters) {
      return [];
    }

    const objectTypeData = getObjectTypeByTag(
      selectedDataObjectTagForAdvancedFilters
    );
    if (!objectTypeData?.translatedUiSchema) {
      return [];
    }

    return getOS3SearchFieldsFromObjectTypeData(objectTypeData);
  }, [selectedDataObjectTagForAdvancedFilters, getObjectTypeByTag]);

  const userTypedAdvancedFilters = useMemo(() => {
    if (!advancedFilterConditions || !advancedFilterConditions.length)
      return "";

    return advancedFilterConditions.map(condition => condition.value).join(",");
  }, [advancedFilterConditions]);

  const previousUserTypedAdvancedFilters = usePrevious(
    userTypedAdvancedFilters
  );

  const userTypedDataObjectAdvancedFilters = useMemo(() => {
    if (
      !dataObjectAdvancedFilters?.conditions ||
      !dataObjectAdvancedFilters.conditions?.length
    )
      return "";

    return dataObjectAdvancedFilters.conditions
      .map(condition => ("value" in condition ? condition.value : ""))
      .filter(condition => condition)
      .join(",");
  }, [dataObjectAdvancedFilters]);

  const previousUserTypedDataObjectAdvancedFilters = usePrevious(
    userTypedDataObjectAdvancedFilters
  );

  useEffect(() => {
    if (
      !userTypedAdvancedFilters ||
      previousUserTypedAdvancedFilters === userTypedAdvancedFilters
    )
      return;

    analyticsService.track(
      AnalyticsEvents.globalSearchEvents.GLOBAL_SEARCH_ADVANCED_FILTER_TYPED,
      {
        filters_length: advancedFilterConditions?.length,
        filter_value: userTypedAdvancedFilters,
        certa_version: "OS3",
        type: "workflow"
      }
    );
  }, [
    userTypedAdvancedFilters,
    previousUserTypedAdvancedFilters,
    advancedFilterConditions?.length
  ]);

  useEffect(() => {
    if (
      !userTypedDataObjectAdvancedFilters ||
      previousUserTypedDataObjectAdvancedFilters ===
        userTypedDataObjectAdvancedFilters
    )
      return;

    analyticsService.track(
      AnalyticsEvents.globalSearchEvents.GLOBAL_SEARCH_ADVANCED_FILTER_TYPED,
      {
        filters_length: dataObjectAdvancedFilters.conditions?.length,
        filter_value: userTypedDataObjectAdvancedFilters,
        certa_version: "OS3",
        type: "data_object"
      }
    );
  }, [
    userTypedDataObjectAdvancedFilters,
    previousUserTypedDataObjectAdvancedFilters,
    dataObjectAdvancedFilters.conditions?.length
  ]);

  const inLabel = intl.formatMessage({
    id: "common.in",
    defaultMessage: "In"
  });
  const whereLabel = intl.formatMessage({
    id: "common.where",
    defaultMessage: "Where"
  });

  const onChange = (props?: [string, string]) => {
    if (!props) {
      clearAdvancedFilters();
      setSelectedResultType(null);
      return;
    }
    const [type, id] = props;
    if (type === "workflows") {
      setSelectedKindIdForAdvancedFilters(id);
      setSelectedResultType(SEARCH_RESULT_TYPES.WORKFLOWS);
    }
    if (type === "dataObjects") {
      setSelectedDataObjectTagForAdvancedFilters(id);
      setSelectedResultType(SEARCH_RESULT_TYPES.DATA_RECORDS);
    }
    if (id !== selectedKindIdForAdvancedFilters) {
      setAdvancedFilterCustomFilter({
        groups: [],
        conditions: [],
        compareType: CompareType.AND
      });
    }
  };

  const options = [
    {
      value: "workflows",
      label: "Workflows",
      children: kindOptions
    },
    {
      value: "dataObjects", // TODO
      label: "Data Records",
      children: dataObjectOptions
    }
  ];

  if (!areAdvancedFiltersVisible) return null;

  return (
    <Stack
      direction="horizontal"
      align="start"
      padding="0 0 0 16"
      dangerouslySetClassName={classNames(
        styles.container,
        "catalystScrollContainer"
      )}
    >
      <Stack direction="horizontal" gap="8" padding="8 0" align="center">
        <Typography
          color={TypographyColors.NEUTRAL_700}
          variant={TypographyVariants.OVERLINE_SMALL}
        >
          {inLabel}
        </Typography>
        <Cascader
          multiple={false}
          label="Type..."
          width="12.875rem"
          minWidth="200px"
          placement="bottomLeft"
          options={options}
          onChange={onChange}
          allowClear
          value={
            selectedSearchTypeForAdvancedFilters
              ? [
                  selectedSearchTypeForAdvancedFilters,
                  selectedKindIdForAdvancedFilters ||
                    selectedDataObjectTagForAdvancedFilters
                ]
              : undefined
          }
          placeholder={"Type..."}
          getPopupContainer={getLayoutContainer}
        />
        {areAdvancedFiltersApplied ? (
          <Typography
            color={TypographyColors.NEUTRAL_700}
            variant={TypographyVariants.OVERLINE_SMALL}
          >
            {whereLabel}
          </Typography>
        ) : null}
      </Stack>
      {selectedSearchTypeForAdvancedFilters === "workflows" &&
      areAdvancedFiltersApplied ? (
        <AdvancedFilters
          advancedFilterCustomFilter={advancedFilterCustomFilter}
          setAdvancedFilterCustomFilter={setAdvancedFilterCustomFilter}
        />
      ) : null}
      {selectedSearchTypeForAdvancedFilters === "dataObjects" &&
      areAdvancedFiltersApplied ? (
        <ObjectsAdvancedFilter
          value={{
            type: "any",
            conditions: dataObjectAdvancedFilters.conditions
          }}
          onChange={setDataObjectAdvancedFilters}
          selectedObjectType={selectedDataObjectTagForAdvancedFilters}
          fieldOptions={fieldOptions}
          shouldEnableGroupQuery={false}
          operatorsByFieldType={OS3_SEARCH_OPERATORS_BY_FIELD_TYPE}
          hideSmartColumns={true}
        />
      ) : null}
    </Stack>
  );
};
